import React from 'react';
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Typography,
  Box,
  Alert,
  IconButton,
  Tooltip,
  DialogContent,
  DialogActions,
  Paper
} from '@mui/material';
import {
  Launch as LaunchIcon,
  InsertChartOutlined as StatsIcon
} from '@mui/icons-material';
import PlatformDialog from '../../../molecules/PlatformDialog/PlatformDialog';
import PlatformDialogTitle from '../../../molecules/PlatformDialog/PlatformDialogTitle';
import {
  operationalRadii,
  operationalScrollbars
} from '../../../../design-system/tokens';

const HypeStatResultsDialog = ({ open, onClose, results }) => {
  if (!results || !results.details) {
    return null;
  }

  const { updated, errors, details } = results;
  const total = details.length;

  const successDomains = details.filter(d => d.status === 'updated');
  const errorDomains = details.filter(d => d.status === 'no_data' || d.status === 'db_error');
  const skippedDomains = details.filter(d => d.status === 'skipped_manual');

  const formatNumber = (num) => {
    if (!num || num === 0) return '-';
    return new Intl.NumberFormat('pt-BR').format(num);
  };

  const getSourceColor = (source) => {
    switch(source) {
      case 'hypestat': return 'success';
      case 'semrush': return 'info';
      case 'similarweb': return 'secondary';
      case 'fallback': return 'warning';
      default: return 'default';
    }
  };

  const getSourceLabel = (source) => {
    switch(source) {
      case 'hypestat': return 'HypeStat';
      case 'semrush': return 'SEMrush';
      case 'similarweb': return 'SimilarWeb';
      case 'fallback': return 'Fallback P10';
      default: return 'Unknown';
    }
  };

  const SectionTitle = ({ children }) => (
    <Typography
      variant="subtitle2"
      sx={{
        fontWeight: 600,
        color: 'primary.main',
        mb: 1.5,
        mt: 1.5,
        textTransform: 'uppercase',
        fontSize: '0.7rem',
        letterSpacing: '0.5px'
      }}
    >
      {children}
    </Typography>
  );

  // Simple, minimalist Table Header Style
  const thStyle = { 
    color: 'text.secondary', 
    fontSize: '0.75rem', 
    fontWeight: 600, 
    borderBottom: '2px solid', 
    borderColor: 'divider',
    py: 1.5
  };

  return (
    <PlatformDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <PlatformDialogTitle
        title="Resultados do Scraping"
        subtitle="HypeStat, SEMrush e SimilarWeb Sync"
        icon={<StatsIcon />}
        onClose={onClose}
        onBack={onClose}
      />

      <DialogContent sx={{ p: { xs: 2, sm: 3 }, ...operationalScrollbars.default }}>
        {/* Resumo */}
        <Box mb={4}>
          <Alert 
            variant="outlined"
            severity={updated > 0 ? 'success' : 'warning'} 
            sx={{ borderRadius: `${operationalRadii.surface}px`, bgcolor: 'transparent' }}
          >
            <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>
              <strong>Total processado:</strong> {total} domínios
              {' • '}
              <strong>Atualizados:</strong> {updated}
              {' • '}
              <strong>Erros:</strong> {errors}
              {skippedDomains.length > 0 && (
                <>
                  {' • '}
                  <strong>Pulados (manual):</strong> {skippedDomains.length}
                </>
              )}
            </Typography>
          </Alert>
        </Box>

        {/* Tabela de Domínios Atualizados */}
        {successDomains.length > 0 && (
          <Box mb={4}>
            <SectionTitle>
              Domínios Atualizados ({successDomains.length})
            </SectionTitle>
            <TableContainer 
              component={Paper}
              variant="outlined"
              sx={{ 
                borderRadius: `${operationalRadii.surface}px`,
                maxHeight: 320,
                ...operationalScrollbars.default
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={thStyle}>Domínio</TableCell>
                    <TableCell sx={thStyle}>Audiência Mensal</TableCell>
                    <TableCell sx={thStyle}>Fonte</TableCell>
                    <TableCell sx={thStyle}>Qualidade</TableCell>
                    <TableCell align="center" sx={thStyle}>Link</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {successDomains.map((item, index) => {
                    const isActual = item.data?.dataQuality === 'actual';
                    
                    return (
                      <TableRow key={index} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {item.domain}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                            {formatNumber(item.data?.monthlyVisits)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getSourceLabel(item.data?.source)}
                            color={getSourceColor(item.data?.source)}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: 22, fontWeight: 500 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={isActual ? 'Real' : 'Parcial'}
                            size="small"
                            variant="outlined"
                            color={isActual ? 'success' : 'warning'}
                            sx={{ fontSize: '0.7rem', height: 22, fontWeight: 500 }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Ver no HypeStat" arrow>
                            <IconButton
                              size="small"
                              onClick={() => window.open(`https://hypestat.com/info/${item.domain}`, '_blank', 'noopener,noreferrer')}
                              sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                            >
                              <LaunchIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Tabela de Domínios com Erro */}
        {errorDomains.length > 0 && (
          <Box mb={4}>
            <SectionTitle>
              Domínios com Erro ({errorDomains.length})
            </SectionTitle>
            <TableContainer 
              component={Paper}
              variant="outlined"
              sx={{ 
                borderRadius: `${operationalRadii.surface}px`,
                maxHeight: 250,
                ...operationalScrollbars.default
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={thStyle}>Domínio</TableCell>
                    <TableCell sx={thStyle}>Motivo</TableCell>
                    <TableCell align="center" sx={thStyle}>Link</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {errorDomains.map((item, index) => (
                    <TableRow key={index} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {item.domain}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="error.main">
                          {item.error || 'Sem dados disponíveis'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Ver no HypeStat" arrow>
                          <IconButton
                            size="small"
                            onClick={() => window.open(`https://hypestat.com/info/${item.domain}`, '_blank', 'noopener,noreferrer')}
                            sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                          >
                            <LaunchIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Domínios Pulados */}
        {skippedDomains.length > 0 && (
          <Box mb={2}>
            <SectionTitle>
              Domínios Pulados (Bloqueio Manual) ({skippedDomains.length})
            </SectionTitle>
            <TableContainer 
              component={Paper}
              variant="outlined"
              sx={{ 
                borderRadius: `${operationalRadii.surface}px`,
                maxHeight: 200,
                ...operationalScrollbars.default
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={thStyle}>Domínio</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {skippedDomains.map((item, index) => (
                    <TableRow key={index} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {item.domain}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Alert 
              severity="info" 
              variant="outlined"
              sx={{ mt: 2, borderRadius: `${operationalRadii.surface}px`, bgcolor: 'transparent' }}
            >
              <Typography variant="caption">
                Estes domínios possuem dados editados manualmente e não foram sobrescritos.
              </Typography>
            </Alert>
          </Box>
        )}

        {/* Nenhum resultado */}
        {details.length === 0 && (
          <Alert severity="info" variant="outlined" sx={{ borderRadius: `${operationalRadii.surface}px`, bgcolor: 'transparent' }}>
            Nenhum resultado processado.
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 }, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button 
          onClick={onClose} 
          variant="contained" 
          fullWidth
          sx={{ 
            fontWeight: 600, 
            borderRadius: `${operationalRadii.control}px`,
            py: 1,
            textTransform: 'none'
          }}
        >
          Fechar Relatório
        </Button>
      </DialogActions>
    </PlatformDialog>
  );
};

export default HypeStatResultsDialog;
